import React, { useState } from 'react';
import {
  Box,
  Typography,
  CardMedia,
  Chip,
  IconButton,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { livresAPI, UPLOADS_URL } from '../../services/api';
import { StatusChip } from '../../utils/statusUtils';
import { TagList, getLivreTagsWithColors } from '../../utils/tagUtils';

function LivreCard({ 
  livre, 
  onEdit, 
  onDelete, 
  onView, 
  onAuthorClick, 
  onEditeurClick, 
  onPhotoClick, 
  availableTags, 
  onTagAdd, 
  onTagRemove, 
  isTagLoading, 
  queryClient,
  isSelected = false,
  onToggleSelection
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const longPressTimerRef = React.useRef(null);
  const hasLongPressedRef = React.useRef(false);
  
  // Détecter si on est sur un device tactile (mobile)
  const isTouchDevice = React.useMemo(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  const handleAuthorClick = (authorName) => {
    if (onAuthorClick) {
      onAuthorClick(authorName);
    }
  };

  const handleEditeurClick = (editeurId) => {
    if (onEditeurClick) {
      onEditeurClick(editeurId);
    }
  };

  const handlePhotoClick = (e) => {
    e.stopPropagation();
    if (onPhotoClick) {
      onPhotoClick(livre.id);
    }
  };

  const handleViewClick = (e) => {
    // Ne pas ouvrir la vue si un clic long vient d'être détecté (sur mobile)
    if (hasLongPressedRef.current) {
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }
    if (onView) {
      onView(livre.id);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === livre.statut_lecture) return;
    
    setIsStatusLoading(true);
    try {
      await livresAPI.update(livre.id, { statut_lecture: newStatus });
      toast.success(t('booksPage.statusUpdateSuccess'));
      // Mettre à jour le cache React Query au lieu de recharger la page
      queryClient.invalidateQueries('livres');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast.error(t('booksPage.statusUpdateError'));
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleCheckmarkClick = (e) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(livre.id);
    }
  };

  // Gestion du clic long sur mobile
  const handleTouchStart = (e) => {
    // Réinitialiser le flag
    hasLongPressedRef.current = false;
    
    // Annuler le timer s'il existe déjà
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
    
    // Démarrer un timer pour le clic long (500ms)
    longPressTimerRef.current = setTimeout(() => {
      hasLongPressedRef.current = true;
      // Empêcher le menu contextuel maintenant qu'on a détecté un clic long
      if (onToggleSelection) {
        onToggleSelection(livre.id);
        // Vibrer légèrement pour donner un feedback (si supporté)
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
      longPressTimerRef.current = null;
    }, 500);
  };

  const handleTouchEnd = (e) => {
    // Annuler le timer si l'utilisateur relâche avant 500ms
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // Si un clic long a été détecté, empêcher le clic normal et le menu contextuel
    if (hasLongPressedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      // Réinitialiser le flag après un court délai
      setTimeout(() => {
        hasLongPressedRef.current = false;
      }, 100);
    }
  };

  const handleTouchMove = (e) => {
    // Annuler le clic long si l'utilisateur bouge le doigt
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Nettoyer le timer au démontage
  React.useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  return (
    <Box 
      onClick={handleViewClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onContextMenu={(e) => {
        // Empêcher le menu contextuel sur mobile lors d'un clic long
        if (hasLongPressedRef.current) {
          e.preventDefault();
        }
      }}
      sx={{ 
        cursor: 'pointer',
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '220px',
        width: '100%',
        transition: 'transform 0.2s ease-in-out',
        position: 'relative',
        // Effet de dézoom et cadre quand sélectionné
        transform: isSelected ? 'scale(0.9)' : 'scale(1)',
        '&:hover': {
          transform: isSelected ? 'scale(0.9)' : 'scale(1.04)',
        },
      }}
    >
      {/* Photo en haut avec statut en haut à droite */}
      <Box 
        sx={{ 
          position: 'relative', 
          width: '100%', 
          aspectRatio: '3/4', 
          overflow: 'hidden', 
          borderRadius: 2,
          zIndex: 1, // Au-dessus du cadre
        }}
      >
        <CardMedia
          component="img"
          image={livre.photo_couverture 
            ? `${UPLOADS_URL}/covers/${livre.photo_couverture}` 
            : '/blank-book.png'}
          alt={livre.titre}
          onClick={handlePhotoClick}
          sx={{ 
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            borderRadius: 2,
            transition: 'filter 0.2s ease-in-out',
            filter: isSelected ? 'blur(1px)' : 'none',
          }}
        />
        
        {/* Voile blanc pour éclaircir quand sélectionné */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0)',
            borderRadius: 2,
            pointerEvents: 'none',
            zIndex: 1,
            transition: 'background-color 0.2s ease-in-out',
          }}
        />
        
        {/* Checkmark de sélection ou cercle vide au hover en haut à gauche */}
        {/* Sur mobile, on n'affiche que si sélectionné. Sur desktop, on affiche aussi au hover */}
        {(isSelected || (!isTouchDevice && isHovered)) && (
          <Box
            onClick={handleCheckmarkClick}
            sx={{
              position: 'absolute',
              top: 9,
              left: 9,
              zIndex: 2,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {isSelected ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                }}
              >
                <CheckIcon
                  sx={{
                    color: 'white',
                    fontSize: 16,
                  }}
                />
              </Box>
            ) : (
              <RadioButtonUncheckedIcon
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: 30,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '50%',
                  padding: '2px',
                }}
              />
            )}
          </Box>
        )}
        
        {/* Statut de lecture en haut à droite */}
        <Box 
          onClick={(e) => e.stopPropagation()}
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8,
            zIndex: 1
          }}
        >
          <StatusChip
            statut={livre.statut_lecture}
            onStatusChange={handleStatusChange}
            isLoading={isStatusLoading}
            editable={true}
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          />
        </Box>
      </Box>
      
      {/* Contenu sous la photo */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 1.5 }}>
        {/* Titre */}
        <Typography 
          variant="h6" 
          component="h3" 
          gutterBottom 
          onClick={handleViewClick}
          sx={{ 
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: 1.4,
            mb: 1,
            color: 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            marginBottom: '4px',
            cursor: 'pointer',
          }}
        >
          {livre.titre}
        </Typography>
        
        {/* Auteurs */}
        {livre.auteurs && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5, marginBottom: '4px' }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' 
              }}
            >
              {livre.auteurs.split(', ').map((author, index) => {
                const authorId = livre.auteurs_ids?.split(',')[index];
                return (
                  <React.Fragment key={index}>
                    {index > 0 && ', '}
                    <Link
                      component="button"
                      variant="body2"
                      color="text.secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAuthorClick(authorId);
                      }}
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        color: '#000000C0',
                        fontSize: '1rem',
                        fontWeight: 400,
                        fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {author}
                    </Link>
                  </React.Fragment>
                );
              })}
            </Typography>
            <Box
              component="span"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              ›
            </Box>
          </Box>
        )}
        
        {/* Emplacement (Bibliothèque > Rayonnage) */}
        {(livre.bibliotheque_nom || livre.rayonnage_nom) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
            <HomeIcon 
              sx={{ 
                fontSize: '0.875rem', 
                color: 'text.secondary', 
                mr: 0.5 
              }} 
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontSize: '12px',
                fontWeight: 400,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' 
              }}
            >
              {livre.bibliotheque_nom || t('booksPage.noLibrary')}
              {livre.rayonnage_nom && ` > ${livre.rayonnage_nom}`}
            </Typography>
          </Box>
        )}
        
        {/* Tags avec leurs couleurs spécifiques */}
        <Box 
          onClick={(e) => e.stopPropagation()}
          sx={{ mt: 0, pt: 1 }}
        >
          <TagList 
            tags={getLivreTagsWithColors(livre)}
            size="small"
            editable={true}
            onTagClick={(tag) => navigate(`/tags/${tag.id}`)}
            onTagRemove={(tag) => onTagRemove && onTagRemove(livre, tag)}
            onTagAdd={(tagId) => onTagAdd && onTagAdd(livre, tagId)}
            availableTags={availableTags || []}
            isLoading={isTagLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default LivreCard;
